<template>
    <div class="puf-payment-page">
        <ProgressBar
            :current-step="3"
        />
        <div class="payment-content">
            <div class="puf-payment__header">
                Enter your payment information
            </div>
            <div class="puf-payment__subheader">
                <img src="@/assets/lock-icon-blue.png" alt="lock" class="list-icon">
                Your payment information is encrypted and will be securely processed.
            </div>
            <div v-if="this.$store.getters.isPrepayment">
                <div id="xifin-container" :class="showPaymentCompletedError ? 'disable' : ''" />
            </div>
            <div v-else id="dropin-wrapper">
                <div id="checkout-message" />
                <div ref="dropinContainer" />
                <button id="submit-button" @click="submitPayment">
                    Submit payment method
                </button>
            </div>
            <div v-if="this.$store.getters.isPrepayment" class="consent">
                By clicking "<span>Pay</span>," you authorize Assurex Health, Inc. to charge ${{ this.$store.getters.amount }} to your payment method for the GeneSight test and agree to <a href="#/privacy/terms" target="_blank">Myriad's Terms of Use</a>.
            </div>
            <div v-else class="consent">
                By clicking "<span>Submit payment method</span>", you authorize Assurex Health, Inc. to charge any remaining balance, up to ${{ this.$store.getters.amount }}, to your payment method after your insurance claim has been processed, and you agree to <a href="#/privacy/terms" target="_blank">Myriad's Terms of Use</a>.
            </div>
            <div v-if="showError" class="payment-error">
                <div>{{ braintreeMessage }}.</div>
                <div>
                    Please double check your payment information. If this issue persists, please contact our Customer Support at <a :href="`tel:${$config.customerSupportPhoneDial}`">{{ $config.customerSupportPhoneDisplay }}</a>.
                </div>
            </div>
            <div v-if="showPaymentCompletedError" class="payment-error">
                An error occurred, please contact our Customer Support at <a :href="`tel:${$config.customerSupportPhoneDial}`">{{ $config.customerSupportPhoneDisplay }}</a>.
            </div>
        </div>
    </div>
</template>

<script>
import PaymentHttp from "../scripts/http/payment-http.js";
import ErrorLogger from "@myriad-neuroscience/common-js";
import ProgressBar from "../components/ui/ProgressBar.vue";
export default {
    name: "Payment",
    components: {
        ProgressBar
    },
    data() {
        return {
            dropinInstance: null,
            refreshTimeout: null,
            message: "",
            showError: false,
            braintreeMessage: "",
            showPaymentCompletedError: false
        };
    },
    mounted() {
        this.$store.dispatch("setShowStartupLoader", false);

        this.$nextTick(() => {
            if (this.$store.getters.isPrepayment) {
                this.initXifin();
            } else {
                this.initDropin();
            }
        });
    },
    beforeDestroy() {
        if (this.dropinInstance) {
            this.dropinInstance.teardown(() => {
                this.dropinInstance = null;
            });
        }

        const xifinContainer = document.getElementById("xifin-container");
        if (xifinContainer) {
            xifinContainer.innerHTML = "";
        }

        if(this.refreshTimeout) {
            clearTimeout(this.refreshTimeout);
        }
    },
    methods: {
        handleBackNavigation() {
            this.$router.push("/payment-review-cost");
        },
        sendBraintreePaymentToServer(data) {
            this.$store.dispatch("setShowStartupLoader", true);
            PaymentHttp.vaultPayment(data).then(response => {
                if (response.data.success) {
                    this.$store.dispatch("setPufIsComplete", true);
                    this.goToThankYouPage();
                } else {
                    this.braintreeMessage = response.data.message;
                    this.showError = true;
                }
            }).catch(()=> {
                this.showError = true;
            }).finally(()=> {
                this.$store.dispatch("setShowStartupLoader", false);
            });
        },
        initDropin() {
            const clientToken = this.$store.getters.braintreeClientToken;
            if (!window.braintree || !clientToken) {
                this.showError = true;
                const wrapped = new Error(
                    `Braintree missing data: braintreescript=${window.braintree}, clientToken=${clientToken}`
                );
                ErrorLogger.logError(config.rootApiUrl + "/ErrorLog", wrapped, "Braintree missing data", "Patient");
                return;
            }

            /* eslint-disable no-undef */
            braintree.dropin.create(
                {
                    authorization: clientToken,
                    container: this.$refs.dropinContainer,
                    card: {
                        cardholderName: {
                            required: true
                        }
                    }
                },
                (err, instance) => {
                    if (err) {
                        this.showError = true;
                        const wrapped = new Error(err);
                        ErrorLogger.logError( config.rootApiUrl + "/ErrorLog", wrapped, "Braintree Drop-in initialization failed", "Patient" );
                        return;
                    }

                    this.dropinInstance = instance;
                }
            );
        },
        submitPayment() {
            if (!this.dropinInstance) {
                return;
            }

            this.dropinInstance.requestPaymentMethod((err, payload) => {
                if (err) {
                    this.showError = true;
                    const wrapped = new Error(err);
                    ErrorLogger.logError( config.rootApiUrl + "/ErrorLog", wrapped, "Failed to get payment method from Braintree", "Patient" );
                    return;
                }

                if (!payload || !payload.nonce || !payload.details) {
                    this.showError = true;

                    const wrapped = new Error("Incomplete Braintree payload");
                    ErrorLogger.logError(
                        config.rootApiUrl + "/ErrorLog",
                        wrapped,
                        "Missing required Braintree payment fields",
                        "Patient"
                    );

                    return;
                }
                
                const month = payload.details.expirationMonth;
                const year = payload.details.expirationYear;
                const expirationDate = month && year ? month + "/" + year : null;
                const paymentData = {
                    paymentMethodNonce: payload.nonce,
                    electronicCardType: payload.details.cardType,
                    expirationDate: expirationDate,
                    cardHolderName: payload.details.cardholderName
                };

                this.sendBraintreePaymentToServer(paymentData);
            });
        },
        goToThankYouPage() {
            this.$router.push("/payment-complete");
        },
        buildXifinScriptUrl(baseUrl) {
            if (!baseUrl || typeof baseUrl !== "string") return "";

            let url = baseUrl.trim();
            let originalURLSuffix = "";

            const timestampParam = "ts=" + new Date().getTime();

            if (url.indexOf("?") !== -1) {
                const existingParams = url.substring(url.indexOf("?") + 1);
                originalURLSuffix = "?" + existingParams + "&" + timestampParam;
                url = url.substring(0, url.indexOf("?"));
            } else {
                originalURLSuffix = "?" + timestampParam;
            }

            if (!url.endsWith("/")) {
                url += "/";
            }

            return url + "static/XIFINEmbeddedPayment.js" + originalURLSuffix;
        },
        async initXifin() {
            this.$store.dispatch("setShowStartupLoader", true);
            var scriptUrl = this.buildXifinScriptUrl(this.$store.getters.xifinConfig.url);
            const isXifinDebugEnabled = String(config.xifinDebugLogging).toLowerCase() !== "false";

            try {
                await this.loadExternalScript(scriptUrl);

                this.paymentPageObj = new window.XIFINEmbeddedPayment();
                const xifinConfig = {
                    elementId: "xifin-container",
                    url: this.$store.getters.xifinConfig.url,
                    debugLogging: isXifinDebugEnabled,
                    "supportedCardTypes": ["VISA", "AMEX", "MasterCard", "Discover"],
                    orgAlias: this.$store.getters.xifinConfig.orgAlias,
                    userId: this.$store.getters.xifinConfig.userId,
                    amount: this.$store.getters.amount.toString(),
                    compactLayout: true,
                    useDefaultCancelButtons: false,
                    securityCodeField: "Required",
                    firstNameField: "Required",
                    lastNameField: "Required",
                    postalCodeField: "Required",
                    addressField: "Required",
                    allowInternationalAddresses: false,
                    echeckEnabled: false,
                    useButtonGradients: false,
                    buttonPrimaryColor: "#0092E1",
                    buttonSecondaryColor: "#0092E1",
                    cancelButtonPrimaryColor: "#6c757d",
                    cancelButtonSecondaryColor: "#6c757d",
                    orderDetails: new this.paymentPageObj.PrePaymentInformation(
                        JSON.stringify({
                            prepayment: {
                                orderId: this.$store.getters.xifinConfig.orderId
                            }
                        }),
                        null,
                        this.$store.getters.xifinPrepaymentToken
                    ),

                    // Required callbacks
                    paymentResultCallback: this.onXifinPaymentResult,
                    finishedLoadingCallback: this.onXifinLoaded,
                    cancelButtonCallback: this.onXifinCancel
                };
                
                this.paymentPageObj.initPaymentFrame(xifinConfig);
                this.refreshTimeout = setTimeout(() => {
                    this.refreshPrepaymentToken();
                }, this.$store.getters.prepaymentTokenExp);
            } catch (err) {
                this.$store.dispatch("setShowStartupLoader", false);
                this.showError = true;
                const wrapped = new Error(err);
                ErrorLogger.logError(config.rootApiUrl + "/ErrorLog", wrapped, "Xifin initialization failed", "Patient");
            }
        },
        loadExternalScript(url) {
            return new Promise((resolve, reject) => {
                if (document.querySelector(`script[src="${url}"]`)) {
                    resolve();
                    return;
                }

                const script = document.createElement("script");
                script.src = url;
                script.async = true;

                script.onload = resolve;
                script.onerror = reject;

                document.body.appendChild(script);
            });
        },
        onXifinLoaded() {
            this.$store.dispatch("setShowStartupLoader", false);
        },
        onXifinCancel() {
            this.$router.push("/payment-review-cost");
        },
        onXifinPaymentResult(result) {
            const success = result && result.transactionInfo && result.transactionInfo.success === true;

            if (success) {
                if(this.refreshTimeout) {
                    clearTimeout(this.refreshTimeout);
                }

                this.sendXifinPaymentCompleted(result);
            } else {
                // Must update the existing iframe with a new prepayment token for every attempt
                this.refreshPrepaymentToken();

                this.showError = true;
                const ti = result && result.transactionInfo ? result.transactionInfo : {};
                const wrapped = new Error(
                    `XIFIN payment failed: result=${ti.result}, message="${ti.responseMessage}", seq=${ti.sequenceNumber}`
                );
                ErrorLogger.logError(config.rootApiUrl + "/ErrorLog", wrapped, "Failed to make payment with Xifin", "Patient");
            }
        },
        sendXifinPaymentCompleted(data, attempt = 1) {
            var MAX_RETRIES = 4;

            if (attempt === 1) {
                this.$store.dispatch("setShowStartupLoader", true);
            }

            PaymentHttp.xifinPaymentCompleted(data).then(response => {
                
                if (response.data.success) {
                    this.$store.dispatch("setShowStartupLoader", false);
                    this.$store.dispatch("setPufIsComplete", true);
                    this.goToThankYouPage();
                } else {
                    throw new Error("API returned failure");
                }
            }).catch(async ()=> {
                if (attempt <= MAX_RETRIES) {
                    const waitTime = Math.pow(2, attempt - 1) * 1000;
                    await this.delay(waitTime);
                    return this.sendXifinPaymentCompleted(data, attempt + 1);
                } else {
                    this.$store.dispatch("setShowStartupLoader", false);
                    this.showPaymentCompletedError = true;
                }
            });
        },
        refreshPrepaymentToken() {
            if(this.refreshTimeout) {
                clearTimeout(this.refreshTimeout);
            }

            this.$store.dispatch("setShowStartupLoader", true);
            PaymentHttp.refreshXifinToken().then(response => {
                if (response && response.data && response.data.success) {
                    this.$store.dispatch("setXifinPrepaymentToken", response.data.token);
                    this.$store.dispatch("setPrepaymentTokenExp", response.data.millisecondsToExpiration);
                    this.paymentPageObj.updatePrepaymentAccessToken(response.data.token);
                    this.refreshTimeout = setTimeout(() => {
                        this.refreshPrepaymentToken();
                    }, response.data.millisecondsToExpiration);
                } else {
                    throw new Error("API returned failure");
                }
            }).finally(() =>{
                this.$store.dispatch("setShowStartupLoader", false);
            });
        },
        delay(ms) {
            return new Promise(resolve => setTimeout(resolve, ms));
        }
    }
};
</script>

<style lang="scss">
  @import "../styles/inputs.scss";

  #xifin-container.disable {
    pointer-events: none;
    opacity: 0.5;
  }

  .puf-payment-page {
    @include site-section();
    padding-bottom: 50px;
    min-height: calc(100vh - 250px);
    padding-top: 43px;
  }

  .back-navigation {
    color: var(--Primary-Primary-Blue, #00A8E9);
    font-size: 20px;
    font-style: normal;
    font-weight: 700;
    line-height: 34px;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    margin-bottom: 20px;

    img {
        margin-right: 10px;
    }
  }

  .payment-content {
    padding: 56px;
    border-radius: 8px;
    border: 1px solid var(--Color-gray-200, #E3E8EF);
  }

  .puf-payment__header {
    color: #000;
    font-family: var(--font-family-Inter, Inter);
    font-size: var(--font-size-hxl, 36px);
    font-style: normal;
    font-weight: 400;
    line-height: 122%;
    letter-spacing: -0.72px;
    margin-bottom: 12px;
  }

  .puf-payment__subheader {
    color: var(--Primary-Grey---Copy-Black, #333);
    font-family: var(--font-family-Inter, Inter);
    font-size: var(--font-size-tl, 18px);
    font-style: normal;
    font-weight: 400;
    line-height: 156%;
    letter-spacing: -0.18px;
    display: flex;
    align-items: center;

    img {
        width: 20px;
        margin-right: 16px;
    }
  }

  #dropin-wrapper {
    max-width: 530px;
  }

  #submit-button {
    background-color: #0092E1;
    border: none;
    color: white;
    padding: 15px 32px;
    text-align: center;
    text-decoration: none;
    display: inline-block;
    margin: 4px 2px;
    cursor: pointer;
    border-radius: 30px;
    font-size: 18px;
    font-style: normal;
    font-weight: 700;
    line-height: 156%; /* 28.08px */
    letter-spacing: -0.18px;
    margin-bottom: 16px;
  }

  .consent {
    color: var(--gray-900, var(--Color-gray-900, #121926));
    font-family: var(--font-family-Inter, Inter);
    font-size: var(--font-size-tm, 16px);
    font-style: normal;
    font-weight: 400;
    line-height: 150%; /* 24px */
    letter-spacing: -0.16px;

    span {
        font-weight: 600;
    }

    a {
        text-decoration: none;
        color: var(--blue-bright-400, var(--Color-blue-bright-400, #39A0F1));
    }
  }

  .payment-error, .payment-error a {
    width: 100%;
    margin-bottom: 0px;
    color: #EF4034;
    max-height: 0;
    font-size: 16px;
    margin-top: 17px;
    text-decoration: none;
  }

  @include small-desktop() {
    .puf-payment-page {
        @include site-section-sm-desktop();
    }
  }
  
  @include tablet() {
    .puf-payment-page {
        @include view-report-tablet-padding();
        min-height: calc(100vh - 381px);
    }
  }

  @include sm() {
    
  }
</style>
